import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTranslation } from 'react-i18next';

const { FiPlus, FiMinus } = FiIcons;

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const { t } = useTranslation();

  const faqs = [
    {
      question: t('Chi si occupa della gestione degli ospiti?'),
      answer: t('Ci pensiamo noi completamente: dalle comunicazioni pre-arrivo all\'accoglienza,dal supporto durante il soggiorno alla gestione del check-out. Il nostro team è disponibile H24 per assistere gli ospiti in qualsiasi necessità.')
    },
    {
      question: t('Cosa devo fare io,da proprietario?'),
      answer: t('Praticamente nulla! Devi solo darci accesso all\'immobile. Ci occupiamo noi di tutto il resto: dalla promozione online alla gestione degli ospiti,dalle pulizie alla manutenzione ordinaria.')
    },
    {
      question: t('Quanto posso guadagnare con un affitto breve?'),
      answer: t('Con la nostra gestione professionale,puoi guadagnare fino al 30-50% in più rispetto all\'affitto tradizionale. I guadagni dipendono dalla location,dalla stagionalità e dalle caratteristiche dell\'immobile.')
    },
    {
      question: t('Esiste un contratto vincolante?'),
      answer: t('No,non esistono vincoli a lungo termine. Il nostro contratto è interrompibile in qualsiasi momento con un semplice preavviso,garantendoti la massima flessibilità.')
    },
    {
      question: t('Come funziona il sistema di pagamenti?'),
      answer: t('Ricevi i tuoi guadagni mensilmente con report dettagliati. Trattiamo la nostra commissione direttamente dalle entrate,garantendo massima trasparenza sui ricavi.')
    },
    {
      question: t('Cosa succede in caso di danni all\'immobile?'),
      answer: t('Gestiamo completamente la procedura: dalla documentazione fotografica alla comunicazione con le piattaforme per il risarcimento danni. Ogni prenotazione è coperta da garanzia.')
    },
    {
      question: t('Su quali piattaforme pubblicate gli annunci?'),
      answer: t('Pubblichiamo su tutte le principali piattaforme: Airbnb,Booking.com,Vrbo,Expedia e molte altre,massimizzando la visibilità del tuo immobile.')
    },
    {
      question: t('Fornite anche servizi di manutenzione?'),
      answer: t('Sì,ci occupiamo della manutenzione ordinaria e coordinare quella straordinaria. Abbiamo una rete di professionisti qualificati per ogni tipo di intervento.')
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-warm-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-5xl font-bold text-warm-900 mb-6"
            >
              {t('Domande Frequenti')}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-warm-700 mb-8 max-w-3xl mx-auto"
            >
              {t('Trova le risposte alle domande più comuni sui nostri servizi di gestione affitti brevi')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div 
                key={index} 
                initial={{ opacity: 0, y: 20 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }} 
                viewport={{ once: true }}
                className="bg-warm-50 rounded-lg shadow-lg overflow-hidden"
              >
                <button 
                  onClick={() => toggleFAQ(index)} 
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-warm-100 transition-colors duration-200"
                >
                  <h3 className="text-lg font-semibold text-warm-900 pr-4">{faq.question}</h3>
                  <SafeIcon 
                    icon={openIndex === index ? FiMinus : FiPlus} 
                    className="h-5 w-5 text-primary-600 flex-shrink-0" 
                  />
                </button>
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }} 
                      animate={{ height: 'auto', opacity: 1 }} 
                      exit={{ height: 0, opacity: 0 }} 
                      transition={{ duration: 0.3 }} 
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-4">
                        <p className="text-warm-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-warm-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }} 
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-warm-900 mb-6">{t('Non Hai Trovato la Risposta?')}</h2>
            <p className="text-xl text-warm-600 mb-8 max-w-2xl mx-auto">
              {t('Il nostro team è sempre disponibile per rispondere alle tue domande specifiche')}
            </p>
            <a 
              href="mailto:info@elviravigliano.com" 
              className="inline-block bg-primary-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-primary-700 transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              {t('Contattaci Direttamente')}
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;